import { TimeSeriesImplV2, Monitoring, Logging } from '@grammarly/telemetry'
import { getConfig } from '../../../config'
import { BUILD_TAG, GR_ENV, IS_DEPLOYED } from '../../../constants'
import { BrowserService } from '../../services/browser'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { getFetchImpl, unloadGuard, stubFetch } from './utils'

type Metric = typeof Monitoring.TimeSeries.rootFemetrics

const { tracking } = getConfig()
const femetricsUrl = tracking.femetrics.url
const appName = 'cms'

class FeMetricsService {
  private static metrics: FeMetricsService
  private _metricsCache: Map<string, Metric>
  private _isInitialized = false
  private _logger?: Logging.Logger

  private constructor() {
    // We store a top-level cache for metrics one level below the root logger.
    // Metrics nested further down aren't used as frequently and hence won't be cached.
    this._metricsCache = new Map()
  }

  public static getInstance(): FeMetricsService {
    if (!this.metrics) {
      this.metrics = new FeMetricsService()
    }

    return this.metrics
  }

  // main use case for fe metrics is on client
  public initOnClient(browserService: BrowserService): void {
    const logger = Monitoring.Logging.getLogger('femetrics')
    const baseContext = {
      app: appName,
      appVersion: BUILD_TAG,
      environment: !IS_DEPLOYED ? 'dev' : GR_ENV,
      browserName: browserService.browserName,
      browserMajorVersion: browserService.majorVersion.toString(),
      deviceType: browserService.deviceType,
    }
    const pageUnloadGuard = unloadGuard()
    const fetchImpl = getFetchImpl() as typeof window.fetch

    this.initMetrics(logger, baseContext, fetchImpl, pageUnloadGuard)
  }

  // we also init on server in order to intercept metrics calls during ssr phase and log a warning
  public initOnServer(): void {
    const logger = Monitoring.Logging.getLogger('femetrics_ssr')
    const baseContext = {
      app: appName,
      appVersion: BUILD_TAG,
      environment: !IS_DEPLOYED ? 'dev' : GR_ENV,
    }
    const fetchImpl = stubFetch((payload) =>
      logger.warn(`Server side wants to send femetric, but isn't allowed to. Payload: ${payload}`)
    ) as typeof fetch

    this.initMetrics(logger, baseContext, fetchImpl, undefined)
  }

  private initMetrics(
    logger: Logging.Logger,
    baseContext: Record<string, string>,
    fetchImpl: typeof fetch,
    unloadGuard?: (_cb: () => void) => void
  ): void {
    if (this._isInitialized) {
      this._logger?.warn('Warning: Femetrics service has already been initialized')
      return
    }
    this._logger = logger

    const rootFemetric = TimeSeriesImplV2.FemetricsStorage.createRoot(
      appName,
      femetricsUrl,
      fetchImpl,
      undefined,
      unloadGuard,
      baseContext
    )

    TimeSeriesImplV2.MetricsConfig.configure(rootFemetric)
    this._isInitialized = true
  }

  private getRootMetric() {
    return Monitoring.TimeSeries.rootFemetrics
  }

  /**
   * This chainable method is used to get a metric logger that emits batched
   * metrics to an femetrics endpoint that pipes data to Victoria Metrics.
   *
   * It can be used like so:
   * ```
   * feMetrics.getMetric(FunnelMetrics.ClientMetrics.BoundaryError)
   *  .getMetric('optionalNestedMetricPrefix')
   *  .getCounter(FunnelFeMetricsActions.show)
   *  .increment()
   * ```
   *
   * @param metrics
   * @returns MetricWithContext
   */
  public getMetric(metricName: CmsMetrics.GeneralClientMetrics) {
    if (!this._isInitialized) {
      this._logger?.error(
        `Trying to get ${metricName} metric, but Femetrics must be initialized before calling getMetric. Using default implementation.`
      )
    }
    if (this._metricsCache.has(metricName) && this._metricsCache.get(metricName)) {
      return this._metricsCache.get(metricName)!
    }

    this._metricsCache.set(metricName, this.getRootMetric().getMetric(metricName))

    return this._metricsCache.get(metricName)!
  }
}

export const feMetrics = FeMetricsService.getInstance()
